import React, { useState } from 'react';
import { IPolicyDocument } from '../../resources/interfaces';
import { UPLOAD_IMAGE_ENDPOINTS, UPLOADABLE_DOCUMENT_TYPES } from '../../resources/enums';
import useImage from '../../hooks/imageHook';
import { removeUnderscoresAndCapitalize, showToast } from '../../utils/miscFunctions';
import SelectMenu from '../form-control/SelectMenu';
import FileUpload from '../elements/FileUpload/FileUpload';
import Button from '../form-control/Button';
import { useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';

interface PayoutAttachmentModalProps {
  policyId: string;
  onProceed: () => void;
  onClose?: () => void;
  documentList: any[];
  policyDocuments: IPolicyDocument[];
  url: UPLOAD_IMAGE_ENDPOINTS;
  policyType: 'auto' | 'nonAuto';
  queryKey?: string;
}

interface UploadRow {
  id: string;
  docType: string;
  file: File | null;
}

const newRow = (): UploadRow => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  docType: '',
  file: null,
});

const docTypeFromLabel = (label: string): UPLOADABLE_DOCUMENT_TYPES => {
  switch (label) {
    case removeUnderscoresAndCapitalize(UPLOADABLE_DOCUMENT_TYPES.CONTRACT_SUMMARY):
      return UPLOADABLE_DOCUMENT_TYPES.CONTRACT_SUMMARY;
    case removeUnderscoresAndCapitalize(UPLOADABLE_DOCUMENT_TYPES.INSURANCE_CERTIFICATE):
      return UPLOADABLE_DOCUMENT_TYPES.INSURANCE_CERTIFICATE;
    case removeUnderscoresAndCapitalize(UPLOADABLE_DOCUMENT_TYPES.ID_CARD):
      return UPLOADABLE_DOCUMENT_TYPES.ID_CARD;
    case removeUnderscoresAndCapitalize(UPLOADABLE_DOCUMENT_TYPES.ID_CARD_BACK):
      return UPLOADABLE_DOCUMENT_TYPES.ID_CARD_BACK;
    case removeUnderscoresAndCapitalize(UPLOADABLE_DOCUMENT_TYPES.PASSPORT_PHOTO):
      return UPLOADABLE_DOCUMENT_TYPES.PASSPORT_PHOTO;
    case removeUnderscoresAndCapitalize(UPLOADABLE_DOCUMENT_TYPES.DRIVER_LICENSE_FRONT):
      return UPLOADABLE_DOCUMENT_TYPES.DRIVER_LICENSE_FRONT;
    case removeUnderscoresAndCapitalize(UPLOADABLE_DOCUMENT_TYPES.DRIVER_LICENSE_BACK):
      return UPLOADABLE_DOCUMENT_TYPES.DRIVER_LICENSE_BACK;
    case removeUnderscoresAndCapitalize(UPLOADABLE_DOCUMENT_TYPES.INSURANCE_STICKER):
      return UPLOADABLE_DOCUMENT_TYPES.INSURANCE_STICKER;
    case removeUnderscoresAndCapitalize(UPLOADABLE_DOCUMENT_TYPES.POLICY_STICKER):
      return UPLOADABLE_DOCUMENT_TYPES.POLICY_STICKER;
    case removeUnderscoresAndCapitalize(UPLOADABLE_DOCUMENT_TYPES.ROADWORTHY):
      return UPLOADABLE_DOCUMENT_TYPES.ROADWORTHY;
    case removeUnderscoresAndCapitalize(UPLOADABLE_DOCUMENT_TYPES.RENEWAL_NOTICE):
      return UPLOADABLE_DOCUMENT_TYPES.RENEWAL_NOTICE;
    default:
      return UPLOADABLE_DOCUMENT_TYPES.CONTRACT_SUMMARY;
  }
};

const UploadDocumentFromDetailsPageModal = ({
  policyId,
  onProceed,
  onClose,
  documentList,
  policyDocuments,
  url,
  policyType,
  queryKey,
}: PayoutAttachmentModalProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [rows, setRows] = useState<UploadRow[]>([newRow()]);
  const { uploadPolicyOwnerDocument, uploadPolicyDocument } = useImage();
  const queryClient = useQueryClient();

  const updateRow = (id: string, patch: Partial<UploadRow>) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const removeRow = (id: string) => {
    setRows((prev) => (prev.length > 1 ? prev.filter((r) => r.id !== id) : prev));
  };

  const addRow = () => setRows((prev) => [...prev, newRow()]);

  const validRows = rows.filter((r) => r.docType && r.file);
  const canSubmit = validRows.length > 0 && validRows.length === rows.length && !loading;

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;

    setLoading(true);
    const uploadFn = policyType === 'nonAuto' ? uploadPolicyOwnerDocument : uploadPolicyDocument;

    const results = await Promise.allSettled(
      validRows.map((r) =>
        uploadFn(r.file as File, docTypeFromLabel(r.docType), url, policyId)
      )
    );

    const successCount = results.filter((res) => res.status === 'fulfilled').length;
    const failedCount = results.length - successCount;

    if (successCount > 0) {
      showToast.success(
        successCount === results.length
          ? `${successCount} document${successCount > 1 ? 's' : ''} uploaded successfully`
          : `${successCount} of ${results.length} documents uploaded — ${failedCount} failed`
      );
      queryClient.invalidateQueries({ queryKey: [queryKey] });
    } else {
      showToast.error('Failed to upload documents');
    }

    setLoading(false);
    onProceed();
  };

  return (
    <div className="p-5 w-full md:w-full">
      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          {rows.map((row, idx) => (
            <div
              key={row.id}
              className="rounded-md border border-gray-200 dark:border-darkBg-border p-4 relative"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-gray-600 dark:text-darkBg-subText uppercase tracking-wide">
                  Document {idx + 1}
                </span>
                {rows.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeRow(row.id)}
                    className="text-red-600 hover:text-red-700 inline-flex items-center gap-1 text-xs"
                    aria-label="Remove document"
                  >
                    <Trash2 className="h-4 w-4" /> Remove
                  </button>
                )}
              </div>
              <div className="mb-3">
                <SelectMenu
                  name={`doc-${row.id}`}
                  label="Select document to upload"
                  listSelectedValue={row.docType}
                  onChange={(value: any) => updateRow(row.id, { docType: value })}
                  search={true}
                  list={documentList || []}
                  required
                />
              </div>
              {row.docType && (
                <FileUpload
                  onFileLoad={(doc: any) => {
                    if (doc && doc[0]) {
                      updateRow(row.id, { file: doc[0] });
                    }
                  }}
                  label=""
                  maxHeight={350}
                  maxWidth={300}
                  imageContainerWidth="100%"
                  cropImage={false}
                  onFileDeleted={() => updateRow(row.id, { file: null })}
                  showFileMax={false}
                  savedImage={null}
                />
              )}
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={addRow}
          className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary-main hover:underline"
        >
          <Plus className="h-4 w-4" /> Add another document
        </button>

        <div className="w-full flex gap-4 mt-6 !mb-4">
          <Button
            type="button"
            label="Cancel"
            onClick={onClose}
            fullWidth
            bgColor="secondary"
          />
          <Button
            type="submit"
            label={
              loading
                ? 'Uploading…'
                : validRows.length > 1
                ? `Upload ${validRows.length} documents`
                : 'Upload'
            }
            fullWidth
            loading={loading}
            disabled={!canSubmit}
            bgColor="primary"
          />
        </div>
      </form>
    </div>
  );
};

export default UploadDocumentFromDetailsPageModal;
